import type { CSSProperties, ReactNode } from 'react'
import { Button, ConfidenceTicks, MetricTable, Text } from '@grovus/design-system'
import type { MetricColumn, MetricSection } from '@grovus/design-system'
import type { Benchmarks, Dimension, ObservedMetrics } from '../creatives/contract'
import type { ConfidenceThresholds } from '../creatives/confidence'
import type { Ratio } from '../creatives/metrics'
import { fill, money, num, plural, ratio } from '../creatives/format'
import { spendShares } from '../creatives/metrics'
import { UNASSIGNED } from '../creatives/partition'
import { glyph, labelOf, markTally, PartialLegend, Unrankable } from '../creatives/view'
import { DICT, type Lang } from '../i18n'
import { versionsOf, type Axis, type BucketRow } from './pool'
import { propose } from './proposal'

/**
 * The comparison's table: every bucket in one ranked list, then the totals row as the final section inside the table - the
 * wireframe's alignment and flow, which no strip beside the table can hold, because
 * `MetricTable` sets no column widths for anything beside it to follow.
 *
 * The active sort is fixed - the comparison ranks, it is not sorted by hand - so no
 * header is sortable and `onRowClick` is not passed: the drill affordance is the real
 * button in the `open` column, one per bucket row, and the totals row carries no
 * affordance and no focus stop.
 */

interface Props {
  t: (typeof DICT)['en']
  lang: Lang
  currency: string | null
  thresholds: ConfidenceThresholds
  dimensions: { hypotheses: Dimension[]; techniques: Dimension[]; messages: Dimension[]; offers: Dimension[]; audiences: Dimension[] }
  benchmarks: Benchmarks
  axis: Axis
  ordered: BucketRow[]
  totals: { metrics: ObservedMetrics; roas: Ratio; cpa: Ratio }
  /** The unscoped response's creative count: additive zero is an empty window's total, never a missing measure's. */
  creativesCount: number
  /**
   * True while the rows on show answer a request this screen is no longer making - a
   * scope or period refetch is in flight, or it failed and left the previous response
   * up. The drill is disabled for the window: a bucket opened from stale rows would
   * land on a creatives list the displayed figures do not describe.
   */
  drillDisabled: boolean
  /** Whether the benchmark these proposals are measured against is the demo one. */
  isDemo: boolean
  onOpenBucket: (axis: Axis, id: string) => void
}

/**
 * The proposal's minimum content width: a 292px column less the cell's 24px of padding.
 *
 * Without it the proposal is the one prose column in the table, so wherever the table
 * overflows its scroller the automatic layout squeezes it to its longest word and the
 * proposal wraps into a column that sets the height of every row (GRO-70). With it the
 * table grows instead, and the scroll hint says so.
 */
const PROPOSAL_MIN_WIDTH = 268

/** The three bar treatments the wireframe drew, `currentColor` throughout, no inline hex. */
function barFill(band: BucketRow['band']): CSSProperties {
  if (band === 'reliable') {
    return { background: 'color-mix(in srgb, currentColor 85%, transparent)' }
  }
  if (band === 'provisional') {
    return { backgroundImage: 'repeating-linear-gradient(90deg, currentColor 0 7px, transparent 7px 9px)' }
  }
  return { backgroundImage: 'repeating-linear-gradient(45deg, currentColor 0 2px, transparent 2px 6px)' }
}

function SpendBar({ bucket, maxSpend }: { bucket: BucketRow; maxSpend: number }) {
  const spend = bucket.metrics.spend ?? 0
  const width = maxSpend > 0 ? Math.max((spend / maxSpend) * 100, 0) : 0
  return (
    <div
      data-testid={`bucket-bar-${bucket.id}`}
      data-band={bucket.band}
      style={{
        height: 16,
        background: 'color-mix(in srgb, currentColor 8%, transparent)',
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${width}%`,
          opacity: bucket.band === 'reliable' ? 0.85 : 0.7,
          ...barFill(bucket.band),
        }}
      />
    </div>
  )
}

export function HypothesesTable({
  t, lang, currency, thresholds, dimensions, benchmarks, axis, ordered, totals, creativesCount, drillDisabled, isDemo, onOpenBucket,
}: Props) {
  // Every hypotheses column is shown, so one key holds the cells and the totals row alike.
  const mark = markTally(t)
  const flat = ordered
  const maxSpend = Math.max(...flat.map((bucket) => bucket.metrics.spend ?? 0), 0)
  const shares = spendShares(flat)
  const shareOf = new Map(flat.map((bucket, index) => [bucket.id, shares[index]] as const))

  const dimensionValues =
    axis === 'hypothesis'
      ? dimensions.hypotheses
      : axis === 'technique'
        ? dimensions.techniques
        : dimensions.audiences

  const unitOf = (count: number) =>
    plural(count, lang, { one: t.unitOne, few: t.unitFew, many: t.unitMany })

  const nameOf = (bucket: BucketRow) =>
    bucket.id === UNASSIGNED
      ? axis === 'hypothesis'
        ? t.hypUnassigned
        : t.techUnassigned
      : (labelOf(dimensionValues, bucket.id, lang) ?? bucket.id)

  function bucketCell(bucket: BucketRow): ReactNode {
    return (
      <span style={{ display: 'block', minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
          <Text size="md" fw={600} component="span" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {nameOf(bucket)}
          </Text>
          {axis === 'hypothesis' && bucket.id !== UNASSIGNED && (
            <Text size="xs" className="grv-muted" component="span" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', flex: 'none' }}>
              {bucket.id}
            </Text>
          )}
        </span>
        <Text size="xs" className="grv-muted" component="div">
          {fill(t.hypSub, { c: bucket.creatives.length, n: bucket.metrics.conversions, u: unitOf(bucket.metrics.conversions ?? 0) })}
        </Text>
        {axis === 'hypothesis' && bucket.id !== UNASSIGNED && versionsOf(bucket).length > 0 && (
          <Text size="xs" className="grv-muted" component="div" data-testid="bucket-versions">
            {fill(t.versionsLine, { v: versionsOf(bucket).join(', ') })}
          </Text>
        )}
      </span>
    )
  }

  function proposalCell(bucket: BucketRow): ReactNode {
    // A verdict attaches to a claim: technique and audience are attributes of the
    // creatives inside a hypothesis, so their cells are empty and the column header
    // stays, so the grid does not reflow between axes. The unassigned bucket on the
    // hypothesis axis is empty for the same reason - `propose` declines it by its own
    // rule, read before the others.
    if (axis !== 'hypothesis') return null
    const outcome = propose(bucket, benchmarks, thresholds)
    if ('withheld' in outcome) return null
    if ('awaiting' in outcome) {
      return (
        <Text size="xs" className="grv-muted" data-testid={`proposal-${bucket.id}`}>
          {fill(t.jWaiting, { n: outcome.awaiting, u: unitOf(outcome.awaiting) })}
        </Text>
      )
    }
    const verdict = outcome.key === 'confirmed' ? t.jvConfirmed : t.jvRefuted
    const basis = fill(isDemo ? t.jBasisDemo : t.jBasis, {
      c: money(outcome.cpa, currency, lang, 2),
      b: money(outcome.benchmark, currency, lang, 2),
      n: outcome.conversions,
      u: unitOf(outcome.conversions),
    })
    return (
      <span data-testid={`proposal-${bucket.id}`}>
        <Text size="sm" fw={600} component="span">
          {t.jProposes}: {verdict}
        </Text>
        <Text size="xs" className="grv-muted" component="div">
          {basis}
          {outcome.provisional ? ` · ${t.jProvNote}` : ''}
        </Text>
      </span>
    )
  }

  function roasCell(bucket: BucketRow): ReactNode {
    const value = bucket.roas.value
    // Muted with a tilde rather than removed when the sample is thin: the figure is
    // what the account reported, and hiding it would leave the reader guessing.
    const body =
      bucket.band === 'insufficient' && value !== null ? (
        <Unrankable
          title={fill(t.defStruckBody, { n: thresholds.provisionalAt })}
          label={`${ratio(value, lang)} - ${t.cannotRank}`}
        >
          {ratio(value, lang)}
        </Unrankable>
      ) : (
        ratio(value, lang)
      )
    return mark.at('cells')(body, bucket.roas.partial)
  }

  function toRow(bucket: BucketRow): Record<string, ReactNode> {
    return {
      // `pool` emits the unassigned sentinel as the id, so every bucket row has a stable
      // `axis:id` string key with no special case.
      key: `${axis}:${bucket.id}`,
      bucket: bucketCell(bucket),
      bar: <SpendBar bucket={bucket} maxSpend={maxSpend} />,
      // A bucket whose spend nobody reported has no share - unknown, not 0% - and every
      // computable share wears the mark when the whole it is cut from is only partly
      // known.
      share: shareOf.get(bucket.id) === undefined
        ? glyph
        : ((share) => (share.pct === null ? glyph : mark.at('cells')(`${share.pct}%`, share.partial)))(shareOf.get(bucket.id)!),
      spend: mark.at('cells')(
        money(bucket.metrics.spend, currency, lang),
        bucket.metrics.incomplete.includes('spend'),
      ),
      n: mark.at('cells')(num(bucket.metrics.conversions, lang), bucket.metrics.incomplete.includes('conversions')),
      roas: roasCell(bucket),
      cpa: mark.at('cells')(money(bucket.cpa.value, currency, lang, 2), bucket.cpa.partial),
      ticks: (
        <ConfidenceTicks
          conversions={bucket.metrics.conversions ?? 0}
          thresholds={thresholds}
          progress
        />
      ),
      // Floored only where a proposal renders: on the secondary cuts the column is empty,
      // and a floor there would add scroll for nothing.
      verdict: ((proposal) =>
        proposal === null ? null : (
          <span style={{ display: 'block', minWidth: PROPOSAL_MIN_WIDTH }}>{proposal}</span>
        ))(proposalCell(bucket)),
      open: (
        <Button
          variant="default"
          size="compact-xs"
          disabled={drillDisabled}
          aria-label={fill(t.hypOpen, { c: bucket.creatives.length })}
          title={fill(t.hypOpen, { c: bucket.creatives.length })}
          data-testid={`bucket-open-${bucket.id}`}
          onClick={() => onOpenBucket(axis, bucket.id)}
        >
          {'\u2192'}
        </Button>
      ),
    }
  }

  const columns: MetricColumn[] = [
    {
      key: 'bucket',
      header:
        axis === 'hypothesis'
          ? t.groupHypothesis
          : axis === 'technique'
            ? t.groupVisual
            : t.groupAudience,
    },
    { key: 'bar', header: t.spendShare },
    { key: 'share', header: '%', numeric: true },
    { key: 'spend', header: t.spend, numeric: true },
    { key: 'n', header: 'n', numeric: true },
    { key: 'roas', header: 'ROAS', numeric: true },
    { key: 'cpa', header: 'CPA', numeric: true },
    { key: 'ticks', header: '' },
    { key: 'verdict', header: t.verdictCol },
    { key: 'open', header: '' },
  ]

  const sections: MetricSection[] = []
  if (ordered.length > 0) {
    sections.push({ key: 'ranked', rows: ordered.map(toRow) })
  }
  // The totals row is the table's final section, inside the table: the wireframe's flow,
  // with no affordance and no focus stop, and the unscoped response's figures. The same
  // rules the bucket rows read hold here: the no-value glyph on a wholly missing measure
  // (an additive zero belongs to an empty window, not to creatives that stayed silent),
  // and the partial mark on every figure a gap touches.
  const emptyWindow = creativesCount === 0
  sections.push({
    key: 'totals',
    rows: [
      {
        key: `${axis}:__totals__`,
        bucket: <Text size="md" fw={700} component="span">{t.allCreatives}</Text>,
        bar: null,
        // The whole is 100% only when the spend it is the whole of is known: a silent
        // or partly reported total is the glyph or a marked figure, not a claim.
        share: totals.metrics.spend !== null && totals.metrics.spend > 0
          ? mark.at('cells')('100%', totals.metrics.incomplete.includes('spend'),
        )
          : glyph,
        // An unknown total still carries its mark. A scope where every creative is
        // uncertain has a null spend *and* a gap, and rendering the bare glyph said only
        // the first half - the reader could not tell it from a measure that does not
        // apply. The empty-window zero holds only while nothing is missing: under a partial
        // scope (D8) an empty window is the glyph, marked.
        spend: emptyWindow && totals.metrics.spend === null && !totals.metrics.incomplete.includes('spend')
          ? money(0, currency, lang)
          : mark.at('cells')(
              money(totals.metrics.spend, currency, lang),
              totals.metrics.incomplete.includes('spend'),
            ),
        n: emptyWindow && totals.metrics.conversions === null && !totals.metrics.incomplete.includes('conversions')
          ? num(0, lang)
          : mark.at('cells')(
              totals.metrics.conversions === null ? glyph : num(totals.metrics.conversions, lang),
              totals.metrics.incomplete.includes('conversions'),
            ),
        roas: mark.at('cells')(ratio(totals.roas.value, lang), totals.roas.partial),
        cpa: mark.at('cells')(money(totals.cpa.value, currency, lang, 2), totals.cpa.partial),
        ticks: null,
        verdict: null,
        open: null,
      },
    ],
  })

  return (
    <div data-testid="hypotheses-table">
      <PartialLegend kinds={mark.kinds()} t={t} />
      <MetricTable
        columns={columns}
        sections={sections}
        rowKey="key"
        scrollHint={t.tableScrollHint}
      />
    </div>
  )
}